import { Injectable } from '@angular/core';
import { MmAlertMode, MmAlertSize, MmAlertType } from '@metromarkets/components';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Order } from '~app/modules/checkout/models';
import { CheckoutService } from '~app/modules/checkout/services';
import {
  CheckVatIdValiditySuccess,
  CreateOrder,
  CreateOrderFailure,
  CreateOrderSuccess,
  OrderActionTypes,
  UpdateOrder,
} from '~app/modules/checkout/store/order/order.actions';
import { ErrorResponse } from '~shared/models';
import { BadRequestHttpError, GeneralHttpError } from '~shared/modules/error-handler';
import { NotificationService } from '~shared/services/notification.service';
import { MmAlertDuration } from '~shared/constants/MmAlertDuration.constants';
import { NotificationApplicationEvent } from '~shared/modules/application-event/models';
import { GaTrackingEventAction, GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { CartState, ShoppingCartActionTypes } from '~shared/modules/shopping-cart-store';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { USER_ROLES } from '~shared/constants/account-info.constant';

@Injectable()
export class OrderEffects {
  @Effect()
  createOrder$ = this.actions$.pipe(
    ofType(OrderActionTypes.CREATE_ORDER),
    switchMap((action: CreateOrder) =>
      this.checkoutService.createOrder(action.payload).pipe(
        map((order: Order) => new CreateOrderSuccess(order)),
        catchError((error) => {
          return this.handleOrderCreationFailure(error);
        })
      )
    )
  );

  @Effect()
  updateOrder$ = this.actions$.pipe(
    ofType(OrderActionTypes.UPDATE_ORDER),
    map((action: UpdateOrder) => action.payload),
    switchMap(({ freightForwardPhone, orderId }) =>
      this.checkoutService.updateOrder(freightForwardPhone, orderId).pipe(
        map((order: Order) => new CreateOrderSuccess(order)),
        catchError((error) => {
          return this.handleOrderCreationFailure(error);
        })
      )
    )
  );

  @Effect()
  checkVatIdValidity$ = this.actions$.pipe(
    ofType(ShoppingCartActionTypes.UPDATE_CART_SUCCESS),
    switchMap(({ payload: cart }: CartState) =>
      this.accountInfoService.getAccountInfoV2().pipe(
        filter(accountInfo => accountInfo !== null),
        take(1),
        map((accountInfo) => {
          const isVATValid = accountInfo.customer?.role === USER_ROLES.consumer || cart?.features?.businessModel === 'B2B';

          if (!isVATValid) {
            this.applicationEventService.publish(
              new NotificationApplicationEvent(
                GaTrackingEventAction.CartNotifications,
                GaTrackingEventLabel.VATIdInvalidOnVies
              )
            );
            this.notificationService.notify({
              title: 'CHECKOUT.REVIEW_PAGE.VALIDATOR.INVALID_VAT_ID_TITLE',
              message: 'CHECKOUT.REVIEW_PAGE.VALIDATOR.INVALID_VAT_ID_TEXT',
              size: MmAlertSize.large,
              type: MmAlertType.warning,
              mode: MmAlertMode.banner,
              duration: MmAlertDuration.default,
            });
          }

          return new CheckVatIdValiditySuccess(isVATValid)
        })
      )
    )
  );

  private handleOrderCreationFailure(error: GeneralHttpError | BadRequestHttpError): Observable<CreateOrderFailure> {
    if (!(error instanceof GeneralHttpError || error instanceof BadRequestHttpError)) {
      return of(new CreateOrderFailure(error));
    }

    const errorMessage = this.getHttpErrorMessage(error);

    this.notificationService.notify({
      message: errorMessage,
      type: MmAlertType.error,
      size: MmAlertSize.large,
    });

    return of(new CreateOrderFailure(error));
  }

  private getHttpErrorMessage(error: ErrorResponse): string {
    const { title } = error;

    let errorMessage = title;

    if (error instanceof BadRequestHttpError) {
      const invalidParamMsg = Object.values(error.invalidParams)[0].reason;

      if (invalidParamMsg) {
        errorMessage = invalidParamMsg;
      }
    }
    return errorMessage;
  }

  constructor(
    private actions$: Actions,
    private checkoutService: CheckoutService,
    private notificationService: NotificationService,
    private applicationEventService: ApplicationEventService,
    private accountInfoService: AccountInfoService,
  ) {}
}
